/**
 * CR140: Scheduler page
 * Admin console for managing scheduled batch runs
 * CR141: Load merchants from canonical source + forums
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { SchedulerList, ScheduleEditor, ScheduleHistory } from "@/components/admin/scheduler";
import { useSchedules } from "@/hooks/useSchedules";
import { supabase } from "@/integrations/supabase/client";
import type { ScheduleWithMerchants, ScheduleFormData, ForumSource } from "@/lib/scheduler/types";

export default function Scheduler() {
  const {
    schedules,
    loading,
    fetchSchedules,
    createSchedule,
    updateSchedule,
    toggleEnabled,
    deleteSchedule,
    runNow,
    stopRun,
  } = useSchedules();

  const [editorOpen, setEditorOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleWithMerchants | null>(null);

  // CR141: Load merchants from canonical source (same as Merchant Sources page)
  const { data: merchants = [] } = useQuery({
    queryKey: ["scheduler-merchants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("merchants")
        .select("merchant_key, display_name, category")
        .order("display_name");
      
      if (error) throw error;
      return (data || []).map(m => ({
        merchant_id: m.merchant_key,
        merchant_name: m.display_name || m.merchant_key,
        category: m.category,
      }));
    },
  });

  // CR141: Load forum sources
  const { data: forumSources = [] } = useQuery({
    queryKey: ["scheduler-forum-sources"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_sources")
        .select("id, name, source_type, endpoint_url, is_enabled")
        .order("name");
      
      if (error) throw error;
      return data as ForumSource[];
    },
  });

  // Load prompt packs
  const { data: promptPacks = [] } = useQuery({
    queryKey: ["scheduler-prompt-packs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_packs")
        .select("id, name")
        .eq("is_active", true)
        .is("deleted_at", null)
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });

  const handleEdit = (schedule: ScheduleWithMerchants) => {
    setSelectedSchedule(schedule);
    setEditorOpen(true);
  };

  const handleCreateNew = () => {
    setSelectedSchedule(null);
    setEditorOpen(true);
  };

  const handleViewHistory = (schedule: ScheduleWithMerchants) => {
    setSelectedSchedule(schedule);
    setHistoryOpen(true);
  };

  const handleSave = async (data: ScheduleFormData): Promise<boolean> => {
    if (selectedSchedule) {
      return updateSchedule(selectedSchedule.id, data);
    } else {
      const id = await createSchedule(data);
      return id !== null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Scheduler</h1>
          <p className="text-muted-foreground">
            Schedule and manage automated batch runs
          </p>
        </div>

        <SchedulerList
          schedules={schedules}
          loading={loading}
          onEdit={handleEdit}
          onDelete={deleteSchedule}
          onToggleEnabled={toggleEnabled}
          onRunNow={runNow}
          onStopRun={stopRun}
          onViewHistory={handleViewHistory}
          onRefresh={fetchSchedules}
          onCreateNew={handleCreateNew}
        />

        <ScheduleEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          schedule={selectedSchedule}
          merchants={merchants}
          forumSources={forumSources}
          promptPacks={promptPacks}
          onSave={handleSave}
        />

        <ScheduleHistory
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          schedule={selectedSchedule}
        />
      </div>
    </AdminLayout>
  );
}
